import { Razor } from "../razor/razor";
import { Browser } from "../core/browser";

export class AuxiliumComponent extends HTMLElement {
  /**
   * How much of a grid a component covers, either everywhere or from a given screen size upwards.
   */
  static spanAttributes = ["column-span", "row-span"]
    .flatMap(span => [span, ...Browser.Breakpoints.map(({ size }) => `${span}-${size}`)]);

  static requiredObservables = ["tooltip", "tooltip-direction", "badge", "identifier", ...this.spanAttributes];
  static requiredBooleans = ["disabled"];
  
  static get observedBooleans() {
    return [...new Set([...this.requiredBooleans, ...(this.observableBooleans ?? [])])];
  }
  
  static get observedAttributes() {
    return [...new Set([
      ...this.requiredObservables,
      ...(this.observable ?? []),
      ...this.observedBooleans
    ])];
  }

  constructor() {
    super();

    this.init = new WeakMap();
    this.queue = [];
    this._onparsed = null;
    this.setQueue = [];
    this.normalizeContent = true;
    this.enhanceContents = this.IsAttributeValueTrue(this.getAttribute("enhance"));
    this.__propertyValues = {};

    for (const attribute of this.constructor.observedAttributes) {
      this._defineProperty(attribute);
    }

    this.identifier ??= crypto.randomUUID().slice(0,8);

    this.#refreshGridPlacement();
  }

  /**
   * A grid works out where its items sit one screen size at a time, so a component covering more
   * than a single column or row leaves the job to whichever grid is holding it.
   */
  #refreshGridPlacement() {
    if (this.parentElement?.nodeName !== "LI") { return; }

    // Reached for from the list item up, so a grid sat inside another one asks the grid holding it.
    this.parentElement.closest("chip-grid")?.refreshPlacement?.();
  }

  #attributeEquals(attr, val) {
    const attrVal = this.getAttribute(attr);
    return attrVal === val?.toString() || (!attrVal && ["null","undefined"].includes(val?.toString()));
  }

  _defineProperty(attribute) {
    const prop = attribute.replace(/-([a-z\d])/ig, (_, c) => c.toUpperCase());

    this.__propertyValues[attribute] = this.getAttribute(attribute);

    Object.defineProperty(this, prop, {
      get: () => this.constructor.observedBooleans.includes(attribute) ? this.hasAttribute(attribute) : this.__propertyValues[attribute],
      set: (val) => {
        if ([true, false].includes(val) && !this.constructor.observedBooleans.includes(attribute)) {
          this.constructor.observedBooleans.push(attribute);
        }

        const updateAttr = () => {
          if (val === false) this.removeAttribute(attribute);
          else if (val === true) this.setAttribute(attribute, "");
          else if (!this.#attributeEquals(attribute, val)) {
            val?.toString().trim() ? this.setAttribute(attribute, val) : this.removeAttribute(attribute);
          }
        };

        this.__propertyValues[attribute] = val;

        this.parsed ? updateAttr() : this.setQueue.push(updateAttr);
      }
    });
  }

  GetValidationMessage(control) {
    if (!control || control.validity.valid) { return this.VALIDATION_CUSTOM; }

    if (control.validity.valueMissing) { return this.VALIDATION_REQUIRED || control.validationMessage; }
    else if (control.validity.typeMismatch) { return this.VALIDATION_TYPE_MISMATCH || control.validationMessage; }
    else if (control.validity.patternMismatch) { return this.VALIDATION_PATTERN_MISMATCH || control.validationMessage; }
    else if (control.validity.tooShort) { return this.VALIDATION_TOO_SHORT || control.validationMessage; }
    else if (control.validity.tooLong) { return this.VALIDATION_TOO_LONG || control.validationMessage; }
    else if (control.validity.rangeUnderflow) { return this.VALIDATION_RANGE_UNDERFLOW || control.validationMessage; }
    else if (control.validity.rangeOverflow) { return this.VALIDATION_RANGE_OVERFLOW || control.validationMessage; }
    else if (control.validity.stepMismatch) { return this.VALIDATION_STEP_MISMATCH || control.validationMessage; }
    else if (control.validity.badInput) { return this.VALIDATION_BAD_INPUT || control.validationMessage; }

    return control.validationMessage;
  }

  InitializeValidationMessages() {
    Object.assign(this, {
      VALIDATION_REQUIRED: this.getAttribute("validation-required") ?? "",
      VALIDATION_TYPE_MISMATCH: this.getAttribute("validation-wrong-type") ?? "",
      VALIDATION_PATTERN_MISMATCH: this.getAttribute("validation-wrong-pattern") ?? "",
      VALIDATION_TOO_SHORT: this.getAttribute("validation-too-short") ?? "",
      VALIDATION_TOO_LONG: this.getAttribute("validation-too-long") ?? "",
      VALIDATION_RANGE_OVERFLOW: this.getAttribute("validation-too-high") ?? "",
      VALIDATION_RANGE_UNDERFLOW: this.getAttribute("validation-too-low") ?? "",
      VALIDATION_STEP_MISMATCH: this.getAttribute("validation-wrong-step") ?? "",
      VALIDATION_BAD_INPUT: this.getAttribute("validation-bad-input") ?? "",
      VALIDATION_CUSTOM: this.getAttribute("validation-custom") ?? ""
    });
  }

  get onparsed() {
    return this._onparsed;
  }

  set onparsed(value) {
    if (this.parsed) { value?.(); }
    else { this._onparsed = value; }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    const tracked = this.__propertyValues?.[name];

    if (typeof tracked !== "boolean" && name in (this.__propertyValues ?? {}) && !this.#attributeEquals(name, tracked)) {
      this.__propertyValues[name] = newValue;
    }

    if (!this.parsed) { return; }

    switch (name) {
      case "tooltip":
        const existing = document.querySelector(`#${this.__tooltipID} .tooltip-content`);

        if (existing) {
          if (newValue) {
            existing.textContent = newValue;
          } else {
            existing.parentElement.remove();
            this.onmouseover = null;
          }
        } else {
          Razor.RefreshTooltips();
        }
        break;
      default:
        if (AuxiliumComponent.spanAttributes.includes(name)) { this.#refreshGridPlacement(); }
        break;
    }

    this.attributeChanged?.(name, oldValue, newValue);
  }

  /**
   * For a boolean attribute, this is a helper function to check whether the attribute should exist or not
   * @param {*} value The value to check
   * @return {boolean} True if the attribute is to exist
   */
  IsAttributeValueTrue(value) {
    return [true, ""].includes(value);
  }


  raiseEvent(eventName, data = {}) {
    const handler = this[`on${eventName.toLowerCase()}`];

    if (handler) {
      handler(data);
    } else {
      this.dispatchEvent(new Event(eventName, {
        view: window,
        bubbles: false,
        cancelable: false
      }));
    }
  }

  raiseEvents(...events) {
    events.forEach(event => this.raiseEvent(event));
  }

  toggleReadonly(setToTrue = false) {
    this.readonly = setToTrue;
  }

  click() {
    this.raiseEvent("click");
  }

  disconnectedCallback() {
    document.getElementById(this.__tooltipID)?.remove();
  }

  #processQueueItem(element, fnName) {
    if (this.init.get(element) || this.parsed) return;
    this.init.set(element, true);

    if (!this.innerHTML?.trim()) { this.innerHTML = ""; }
    if (this.firstChild?.nodeType === Node.TEXT_NODE && this.normalizeContent) { this.firstChild.trimBreaks(); }
    if (this.enhanceContents) { this.enhance(); }

    element[fnName]?.();
    this.parsed = true;

    this.setQueue.forEach(fn => fn());

    if (this.tooltip || this.querySelector("[tooltip]")) Razor.RefreshTooltips();

    queueMicrotask(() => this.raiseEvent("parsed"));
  }

  connectedCallback() {
    const processQueue = () => {
      this.queue = this.queue.filter(([_, fnName]) => fnName !== "parsedCallback");
      this.queue.push([this, "parsedCallback"]);

      if (!this.queue.length) return;
      const queued = this.queue.splice(0);
      requestAnimationFrame(() => queued.forEach(([el, fnName]) => this.#processQueueItem(el, fnName)));
    };

    const isParsed = el => {
      while (el) {
        if (el.nextSibling) return true; el = el.parentNode;
      }

      return false;
    };

    this.init.set(this, false);
    const readyOrParsed = () => isParsed(this) || this.ownerDocument.readyState === 'complete';

    if (readyOrParsed()) {
      processQueue();
      return;
    }

    const onReady = () => {
      observer?.disconnect();
      this.ownerDocument.removeEventListener("DOMContentLoaded", onReady);
      processQueue();
    };

    this.ownerDocument.addEventListener("DOMContentLoaded", onReady);

    const observer = new MutationObserver(() => readyOrParsed() && onReady());

    if (this.parentNode) {
      observer.observe(this.parentNode, { childList: true, subtree: true });
    }
  }
}
